import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { imageUrl, type ChatImage } from '@engineer-os/chat';
import { chatUrl } from '../../lib/chat';
import { MAX_EDGE } from '../../lib/image';
import { useStrings } from '../../state/useLocale';

/**
 * The two places a picture somebody sent is drawn: in the row, and full size.
 *
 * They are one file because they are one decision three times over — where the
 * bytes come from, what the box is before they arrive, and what is said about
 * a picture nobody described — and splitting them would have meant answering
 * each of those twice and hoping the two answers stayed the same.
 *
 * ## The box is reserved, always, before anything loads
 *
 * `w` and `h` came from the tab that uploaded the picture and were bounded but
 * never verified by the Worker — there is no decoder on the server and there
 * will not be one for a layout hint (packages/chat, {@link ChatImage}). So
 * they are treated here the way every other number off the socket is treated:
 * used for what they are for, and not trusted to be sane. {@link side} turns
 * anything that is not a positive integer into the ceiling the uploader works
 * to, which gives a square box for a message that lied rather than a division
 * by zero or an `aspect-ratio: 400 / 0` the browser throws away.
 *
 * The reservation itself is the `width`/`height` attributes and an explicit
 * `aspect-ratio` saying the same thing twice. The attributes alone would do it
 * in every browser this ships to — they are what gives a replaced element its
 * intrinsic size before the bytes land — and the property is there because it
 * is the thing a reader of this file will look for, and because it survives a
 * future where something sets `width` in CSS and silently takes the attribute
 * out of the sizing.
 *
 * Neither picture is ever drawn bigger than it was sent. A 640px WebP scaled
 * to fill a 1400px window is a blurred 640px WebP, and the full-size view
 * exists to be *read*, not to be zoomed into (lib/image.ts, `MAX_EDGE`).
 *
 * ## `alt`, for a picture nobody described
 *
 * There is no honest description available. Nobody typed one, and this file is
 * not going to invent one. `alt=""` — which is what the avatar next to a name
 * correctly uses (Identity.tsx), because the name beside it already says
 * everything the picture says — would be a lie here: it means "skip this, it
 * repeats the text", and there is no text. A room where the only content of a
 * message is announced as decoration is a room a screen reader cannot follow.
 *
 * So the `alt` says the one true thing: there is a picture here and nobody
 * described it. That is short enough to be a name and honest enough to act on
 * — a reader can ask the room what is in it, which is the correct next move
 * and the one an empty `alt` would have hidden.
 *
 * It is also, deliberately, the accessible name of the button the thumbnail
 * lives in: no `aria-label` on that button, for exactly the reason
 * `Identity.tsx` gives for `.msg-who` — a label there would replace the one
 * thing worth hearing with a description of the control. The description goes
 * in the `title`, which is the arrangement every other pressable thing in this
 * panel already uses.
 */

/**
 * One side of the box, from a number that arrived over a socket.
 *
 * Positive, whole, and no larger than anything the uploader can produce.
 * Anything else — zero, negative, fractional, `NaN`, absent because an old
 * client never sent it — becomes the ceiling, so the two sides fall back to a
 * square. A learner who lies about the shape gets their own picture in a
 * badly-shaped box, which is where that cost belongs.
 */
function side(n: number): number {
  return Number.isFinite(n) && n >= 1 ? Math.min(Math.round(n), MAX_EDGE) : MAX_EDGE;
}

/**
 * Where the bytes come from.
 *
 * `imageUrl` is a path, because the package that owns the route has no opinion
 * about origins. The panel does: a deployment can point the whole chat at a
 * different host (`VITE_CHAT_ORIGIN`), the upload already goes through
 * `chatUrl` for that reason (lib/image.ts), and an `<img>` left on a bare path
 * would be the one request in the feature that went to the wrong place.
 */
function src(image: ChatImage): string {
  return chatUrl(imageUrl(image.id)).href;
}

/**
 * The picture in the message row.
 *
 * A button, because pressing it opens the full-size view, and a button is the
 * only thing in a document that both a pointer and a key can press. Enter and
 * Space come free with it — there is no key handler here and there should not
 * be one.
 *
 * A 404 is expected rather than exceptional: the object may have been swept,
 * reported and destroyed, or deleted with the account that posted it, and the
 * message that carries the id outlives all three. What a reader gets then is a
 * sentence, not the browser's broken-image glyph — the glyph says "this page
 * is broken", and the true statement is narrower and less alarming than that.
 * The box is given up at the same moment: there is nothing left to reserve it
 * for, and holding three hundred pixels of nothing open would be the layout
 * insisting on a picture that is gone.
 */
export function MessageImage({ image, onOpen }: { image: ChatImage; onOpen(): void }) {
  const s = useStrings().chat;
  const [broken, setBroken] = useState(false);
  const w = side(image.w);
  const h = side(image.h);

  if (broken) return <p className="chat-image-gone">{s.image.gone}</p>;

  return (
    <button type="button" className="chat-image" title={s.image.open} onClick={onOpen}>
      <img
        src={src(image)}
        alt={s.image.alt}
        width={w}
        height={h}
        style={{ aspectRatio: `${w} / ${h}` }}
        /* A room holds a thousand messages and a learner arriving at a
           permalink is dropped into the middle of them. Neither of these is an
           optimisation: without them, opening a busy room fetches and decodes
           every picture in it on the main thread the emulator is running on. */
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
      />
    </button>
  );
}

/**
 * The picture, as big as it was sent and as big as the screen allows.
 *
 * ### Why this is a scrim over the page and not a panel takeover
 *
 * The drawer's own dialogs — report, delete, the profile card — are a
 * takeover: `ChatPanel` early-returns a different tree in place of the
 * conversation. That is the right shape for a form, and the wrong shape for
 * this, for a reason that has nothing to do with taste: the panel is between
 * 280 and 900 pixels wide because the learner dragged it there, and a picture
 * that is not readable at 280 is not made readable by being drawn at 280 with
 * the messages hidden behind it. The whole point of opening it is to leave the
 * panel's width behind.
 *
 * ### Why it is a portal
 *
 * `.chat` is `position: fixed` with `z-index: 84`, which makes it a stacking
 * context. Nothing rendered inside it can ever paint above `.chat-toggle` at
 * 85, whatever z-index it is given — so a scrim rendered in place would have
 * the chat bubble floating on top of it, and the one control a reader would
 * reach for to dismiss the picture would instead close the entire chat out
 * from under them. A portal to `document.body` is the only way out of that,
 * and this is the first one in the app: it is here because the stacking
 * context is real, not because portals are tidy.
 *
 * At 86 it clears both the panel and the bubble by one, which is how every
 * other value in chat.css was chosen — 84 is "above the resize handles, below
 * the toast", 85 is "over the level-clear overlay". Nothing is added to the
 * gap that is not needed.
 *
 * ### The manners
 *
 * `ClearOverlay.tsx` is the reference and this follows it: the element that
 * had focus is remembered, the dialog itself takes focus rather than the close
 * button, Tab and Shift+Tab cycle inside, Escape closes, a click on the scrim
 * closes only when it landed on the scrim, and focus goes back by hand on the
 * way out.
 *
 * Two departures, both forced by the portal.
 *
 * Escape is handled twice — once as a React key handler on the scrim, once on
 * the document. React dispatches events from a portal through the *component*
 * tree, so an Escape in here reaches `ChatDock`'s handler, which closes the
 * whole drawer. The React handler is what gets there first and stops it, the
 * house rule being that Escape unwinds one layer at a time (MessageMenu.tsx
 * says the same thing about its own). Stopping it also stops the native event
 * short of the document, so the second handler cannot double-fire; it is there
 * for the one case the first cannot see, which is a keystroke arriving while
 * focus sits on `<body>` rather than inside the dialog.
 *
 * And focus is only restored to an element still in the document. The clear
 * overlay is unmounted by its own close and never by its parent disappearing;
 * this is inside a drawer that can be shut with Escape or Ctrl+/, and in that
 * order `ChatDock` puts focus on the toggle *before* this unmounts. Focusing a
 * detached node afterwards does not throw — it silently moves focus to
 * `<body>`, which is the one place a keyboard user cannot get out of.
 */
export function ImageView({ image, onClose }: { image: ChatImage; onClose(): void }) {
  const s = useStrings().chat;
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  const [broken, setBroken] = useState(false);
  const w = side(image.w);
  const h = side(image.h);

  useEffect(() => {
    restoreTo.current = document.activeElement as HTMLElement | null;
    // The dialog itself, not the close button: a keyboard user should hear
    // what opened before they are standing on the way out of it.
    panelRef.current?.focus();
    return () => {
      const back = restoreTo.current;
      if (back?.isConnected) back.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      // The panel holds focus on open and is deliberately not in the list
      // above. Without this the first Shift+Tab falls through to the browser,
      // focus escapes behind the scrim, and because it is then neither `first`
      // nor `last` the trap can never take it back.
      const active = document.activeElement as HTMLElement | null;
      if (!active || active === panel || !panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    // Ours before the drawer's: one Escape puts the picture away, a second
    // closes the chat.
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  return createPortal(
    <div
      className="chat-lightbox"
      onKeyDown={onKeyDown}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="chat-lightbox-panel" ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={s.image.full}>
        <button
          type="button"
          className="chat-x chat-lightbox-x"
          aria-label={s.image.close}
          title={s.image.closeWhy}
          onClick={onClose}
        >
          <svg viewBox="0 0 12 12" aria-hidden="true" width="12" height="12">
            <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </button>
        {broken ? (
          <p className="chat-image-gone">{s.image.gone}</p>
        ) : (
          <img
            className="chat-lightbox-img"
            src={src(image)}
            alt={s.image.alt}
            width={w}
            height={h}
            style={{ aspectRatio: `${w} / ${h}` }}
            /* No `loading="lazy"`: this one is on screen the moment it exists,
               and deferring the only picture the reader asked for would be the
               attribute working against its own purpose. */
            decoding="async"
            onError={() => setBroken(true)}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

export default ImageView;
